import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Grid,
} from "@mui/material";
import { useAuth } from "../../firebase";
import { FormTypes } from "../shop/productTypes";
import { useNavigate } from "react-router-dom";
import { getFormPath } from "../../AppRouter";
import { styled } from "@mui/material/styles";
import OrderCard, {
  OrderData as CardOrderData,
} from "../../components/OrderCard";
import { ORDER_ACTION_LABELS } from "../../constants/orderActions";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
} from "firebase/firestore";
import { db } from "../../firebase";

const OrderItemContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  width: "100%",
  justifyContent: "space-between",
});

const SectionPaper = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

// Type for order data (matches CardOrderData)
type OrderData = CardOrderData;

interface OrdersTabProps {
  onOrderCountChange?: (count: number) => void;
}

const OrdersTab: React.FC<OrdersTabProps> = ({ onOrderCountChange }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [pastOrders, setPastOrders] = useState<OrderData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { user } = useAuth();
  const userId = user ? user.uid : null;

  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      setOrders([]);
      setPastOrders([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    // Create query for user's orders
    const ordersRef = collection(db, "orders");
    const q = query(
      ordersRef,
      where("userId", "==", userId),
      orderBy("createdAt", "desc")
    );

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        try {
          const allOrders: OrderData[] = [];

          querySnapshot.forEach((doc) => {
            const data = doc.data();

            // Validate required fields
            if (!data.userId || !data.status) {
              console.warn(`Order ${doc.id} missing required fields:`, data);
              return;
            }

            // Validate items array
            if (!Array.isArray(data.items) || data.items.length === 0) {
              console.warn(
                `Order ${doc.id} has invalid or empty items array:`,
                data.items
              );
            }

            const order: OrderData = {
              paypalOrderId: doc.id,
              userId: data.userId,
              status: data.status,
              amount: data.amount || 0,
              currency: data.currency || "USD",
              createdAt: data.createdAt,
              completedAt: data.completedAt || null,
              items: Array.isArray(data.items) ? data.items : [],
              formStatus: data.formStatus || {},
              formData: data.formData || {},
              actionsRequired: Array.isArray(data.actionsRequired)
                ? data.actionsRequired
                : [],
              shippingAddress: data.shippingAddress || undefined,
              notes: data.notes || "",
            };
            allOrders.push(order);
          });

          // Define current vs past orders by status
          const currentStatuses = ["pending", "awaiting-info", "in-progress"];
          const currentOrders = allOrders.filter((order) =>
            currentStatuses.includes(order.status)
          );
          const completedOrders = allOrders.filter(
            (order) =>
              order.status === "completed" || order.status === "canceled"
          );

          setOrders(currentOrders);
          setPastOrders(completedOrders);

          if (onOrderCountChange) {
            onOrderCountChange(currentOrders.length);
          }

          setError(null);
        } catch (err: any) {
          console.error("Error processing orders:", err);
          setError(err.message || "Error loading orders");
        } finally {
          setIsLoading(false);
        }
      },
      (err) => {
        console.error("Error fetching orders:", err);
        setError("Failed to load orders");
        setIsLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [userId, onOrderCountChange]);

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 2, sm: 3, md: 4 } }}>
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 4,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
            Loading orders…
          </Typography>
        </Box>
      )}
      {error && (
        <Typography variant="body2" color="error" sx={{ p: 2 }}>
          {error}
        </Typography>
      )}
      {/* Current Orders */}
      <SectionPaper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontWeight: "bold", fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
        >
          Current Orders
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {orders.length === 0 ? (
          <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
            You have no current orders.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 3,
              mt: 2,
            }}
          >
            {orders.map((order) => (
              <OrderCard
                key={order.paypalOrderId}
                order={order as CardOrderData}
                onPrimaryAction={(id) => {
                  /* TODO: implement navigation based on nextAction */
                  console.log("Primary action for", id);
                }}
                onSecondaryAction={(id) => {
                  navigate("/support");
                }}
              />
            ))}
          </Box>
        )}
      </SectionPaper>

      {/* Past Orders */}
      <SectionPaper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontWeight: "bold", fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
        >
          Past Orders
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
            gap: 2,
            mt: 2,
          }}
        >
          {/* If pastOrders is empty, show a helpful message */}
          {pastOrders.length === 0 ? (
            <Typography variant="body1" color="textSecondary">
              You have no past orders.
            </Typography>
          ) : (
            /* Dynamically create ListItems from pastOrders */
            pastOrders.map((order) => (
              <Box
                key={order.paypalOrderId}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  boxShadow: 1,
                  backgroundColor: "background.paper",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
                  Order #{order.paypalOrderId}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 1 }}
                >
                  Completed on{" "}
                  {order.completedAt
                    ? new Date(order.completedAt).toLocaleDateString()
                    : "N/A"}
                </Typography>
                <Button fullWidth variant="outlined" color="primary">
                  Reorder
                </Button>
              </Box>
            ))
          )}
        </Box>
      </SectionPaper>
    </Box>
  );
};

export default OrdersTab;
